<template>
  <v-container class="app" grid-list-md fluid @click="keepSessionActive()" id="fcc-container">

    <!-- dialogs -->    
    <cost-comparison-detail 
      :show="showCostComparisonDetail"
      :markets="markets"
      :marketParams="marketParams"
      v-on:did-apply="applyDetail"
      v-on:did-cancel="showCostComparisonDetail=false"
    >
    </cost-comparison-detail>

    <pdf-report-viewer 
      :show="showPdfReportViewer"
      :pdfUrl="generatedPdfReport"
      v-on:close-dialog="showPdfReportViewer=false"
    >
    </pdf-report-viewer>
    
    <session-usage-survey
      :show="showSessionUsageSurvey"
      :sessionToUpdate="lastIncompleteSession"
      v-on:did-apply="applySessionUsageSurvey"
      v-on:close-dialog="closeSessionUsageSurvey"
    >
    </session-usage-survey>
    
    <file-import-status-dialog
      :show="showFCCImportStatus"
      :isLoading="fccImportInProgress"
      :errors="fccImportErrors"
      v-on:close-dialog="showFCCImportStatus=false"
    >
    </file-import-status-dialog>

    <div class="grid">
      <div id="return-button">
        <v-btn 
          color="grey lighten-2"
          icon 
          class="nav-up-button"
          @click="goToLanding()"
          light
          fab
          >
          <v-icon>keyboard_arrow_left</v-icon>
        </v-btn>
        <span
          class="clickable"
          @click="goToLanding()"
        >
          Home
        </span>
      </div>
      <div id="header" ref="header">
        <v-card class="input-card">
          <span class="adjust-section-title section-title hidden-xs-only">
            Compare costs in different markets
          </span>
          <span class="adjust-section-title section-title hidden-sm-and-up">
            Compare costs
          </span>

          <div class="input-card-content">
            <v-layout row pt-3>
              <range-adjustment
                ident="totalSqft"
                title="TOTAL SQUARE FOOTAGE"
                :value="250000"
                :steps="[25000,50000,75000,100000,125000,150000,200000,250000, 300000, 400000, 500000, 600000, 700000, 800000, 900000, 1000000]"
                v-on:value-changed="updateSquareFootage"
              ></range-adjustment>

              <div class="vertical-separator"></div>

              <range-adjustment
                ident="headCount"
                title="HEAD COUNT PER WAREHOUSE"
                :value="60"
                :steps="[10,20,30,40,50,60,70,80,90,100,200,300,400,500,600,700,800,900,1000]"
                v-on:value-changed="updateHeadCount"
              ></range-adjustment>

              <div class="vertical-separator"></div>

              <range-adjustment
                ident="containersPerYear"
                title="CONTAINERS PER YEAR"
                :value="1200"
                :steps="[100,200,300,400,500,600,700,800,900,1000,1250,1500,1750,2000,2500,3000,3500,4000,5000,10000,15000,20000 ]"
                v-on:value-changed="updateContainersPerYear"
              ></range-adjustment>
            </v-layout>
          </div>

          <div class="adjust-actions">
            <a @click="showCostComparisonDetail=true" class="pr-1 pl-1">Cost Comparison Detail</a>
            <span style="color: #aaa">|</span>

            <a @click="generatePdfReport()" class="pl-1">Generate PDF</a>
          </div>

          <help-box class="help-box" topic="fcc"></help-box>
        </v-card>
      </div>

      <div id="graph1" ref="graph1" v-bind:style="{height: doubleRowHeight}">
        <v-card class="your-costs-view">
          <span class="section-title">
            Total Cost Comparison
          </span>
          <span class="section-title-action">
            <a @click="didSelectExport">Export</a>
            <span style="color: #aaa"> | </span>
            <a @click="didSelectImport">Import</a> 
            <input 
              type="file" 
              ref="hiddenImportFileInput" 
              style="display:none"
              @change="importFileDialogClosed"
              accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
            >
          </span>
          
          <total-cost-comparison-chart 
            class="your-costs-chart"
            :series="marketTotalCostComparison"
          ></total-cost-comparison-chart>
        </v-card>
      </div>
      <!-- <div id="graph2" ref="graph2" v-bind:style="{height: rowHeight}">
        <v-card class="your-service-view">
          <span class="section-title">
            Cost Breakdown Comparison
          </span>
          <cost-breakdown-comparison-chart 
            class="your-service-chart"
            :series="marketBreakdownCostComparison"
          ></cost-breakdown-comparison-chart>
        </v-card>
      </div> -->

      <div id="facility-view" ref="facilityView" v-bind:style="{height: rowHeight}">
        <v-container text-xs-center pt-0 pb-2 fluid>
          <v-btn
            color="#f00"
            small    
            center
            flat
            @click="addLocationToCompare()"
          >
            <v-icon
              color="blue darken-4"
            >
              add
            </v-icon>
            <pre> </pre>location to compare
          </v-btn>
        </v-container>

        <div id="facility-list" class="pt-0">
          <v-container grid-list-xs text-xs-center pa-0>
            <v-layout row wrap>
              <v-flex xs12 pl-1 pr-2 ml-1 mr-1 v-for="(f, idx) in markets" v-bind:key="`market:${idx}`">
                <div>
                  <market-costs-card
                    :ident="String(idx)"
                    class="market-cost-card"
                    :markets=availableMarkets
                    :selectedMarket=f
                    :enableDelete=true
                    :randomId=customMarkets[idx].id
                    :customMarket=customMarkets[idx]
                    v-on:delete-card="deleteLocation(idx)"
                    v-on:selected-market-did-change="selectedMarketDidChange"
                  >
                  </market-costs-card>
                </div>
              </v-flex>
            </v-layout>
          </v-container>
        </div>
      </div>

      <div id="sbmap" ref="sbmap" v-bind:style="{height: rowHeight}">
        <v-card class="map-view">
          <span class="section-title">
            Your network
          </span>
          <span class="navigator-link">
            <!--<a href="https://starboardcorp.com/jll" target="_blank">Build a network in JLL Navigator</a>-->
          </span>
          <hr />
          <starboard-network-visualizer 
            class="map"
            :nodes="nodes"
            :lanes="[]"
            v-on:zoom-changed="updateZoom"
            v-on:center-changed="updateCenter"
          />
        </v-card>
      </div>
    </div>
      
    <div v-bind:style="{position: 'fixed', bottom: pdfChartStyle.bottom}">
        <total-cost-comparison-chart 
          class="pdf-your-costs-chart"
          :series="marketTotalCostComparison"
          ref="pdfYourCostsChart"
          v-bind:style="{height: pdfChartStyle.height, width: pdfChartStyle.width}"
        ></total-cost-comparison-chart>
    </div>
    <span class="rates-updated">{{ availableMarkets[0] ?  'Lease Rates updated as of ' + availableMarkets[0].laborRateUpdated + '. Labor Rates updated as of ' + availableMarkets[0].laborRateUpdated + '.' : "" }}</span>
  </v-container>
</template>

<script>
import _ from 'lodash'
import fileDownload from 'js-file-download'
import logoImage from '@/assets/jll-logo.png'

export default {
  data () {
    return {
      innerHeight: 0,
      innerWidth: 0,
      calculatedHeight: 0,
      showCostComparisonDetail: false,
      showPdfReportViewer: false,
      showSessionUsageSurvey: false,
      totalSquareFootage: 250000,
      headCountPerWarehouse: 60,
      containersPerYear: 1200,
      generatedPdfReport: String,
      logoImage: logoImage,
      pdfMapOptions: {
        zoomLevel: 0,
        mapCenter: {lat:38.768403, lng:-96.0},
        styleParams: ""
      },
      resolveSessionUsageData: {},
      sessionRefreshingInterval: null,
      sessionIdleTimeout: null,
      lastIncompleteSession: null,
      showFCCImportStatus: false,
      fccImportInProgress: false,
      fccImportErrors: [],
      pdfChartStyle: {
        width: "1312px",
        height: "700px",
        bottom: "-750px"
      }
    }
  },

  beforeMount: function() {
    _.forEach(this.markets, o => {
      this.updateMarketParams(o.market, this.markets)
    })
  },
  mounted: function () {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    this.getMarketComparisonData()
    
    this.pdfMapOptions.styleParams = this.$store.getters.pdfMapStyleParams
    
    this.sessionRefreshingInterval = this.startSessionRefreshingInterval()
    this.$store.dispatch('initializeSessionDetails')
    this.$store.dispatch('updateSessionAppUsed','FC')
    this.$store.dispatch('getAvailableMarkets')
    this.$store.dispatch('getCustomers')
    this.keepSessionActive()
    
    let fileDropArea = document.getElementById('fcc-container')
    let preventEvent = function(e) {
      e.preventDefault()
      e.stopPropagation()
    }
    fileDropArea.addEventListener('dragenter', preventEvent, false)
    fileDropArea.addEventListener('dragover', preventEvent, false)
    fileDropArea.addEventListener('dragleave', preventEvent, false)
    fileDropArea.addEventListener('drop', this.handleImportDrop, false)
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
    this.cancelSessionRefreshingInterval()
  },

  components: {
    'help-box': () => import ('@/components/HelpBox'),
    'market-costs-card': () => import ('@/components/MarketCostsCard'),
    'total-cost-comparison-chart': () => import ('@/components/TotalCostComparisonChart'),
    'cost-breakdown-comparison-chart': () => import ('@/components/CostBreakdownComparisonChart'),
    'cost-comparison-detail': () => import ('@/components/CostComparisonDetail'),
    'range-adjustment': () => import ('@/components/RangeAdjustment'),
    'starboard-network-visualizer': () => import ('@/components/StarboardNetworkVisualizer'),
    'pdf-report-viewer': () => import ('@/components/PdfReportViewer'),
    'session-usage-survey': () => import ('@/components/SessionUsageSurvey'),
    'file-import-status-dialog': () => import('@/components/FileImportStatusDialog'),
  },

  watch: {
    markets() {
      this.getMarketComparisonData()
    },
  },

  computed: {
    markets() {
      return this.$store.getters.marketsToCompare
    },
    marketParams() {
      return this.$store.getters.marketParams
    },
    customMarkets() {
      return this.$store.getters.customMarkets
    },
    rowHeight() {
      if(this.innerWidth < 600   ) return "325px"
      else if(this.innerHeight < 550) return "400px"
      else return `${this.calculatedHeight}px`
    },
    doubleRowHeight() {
      return  `${this.calculatedHeight * 2 + 10}px`
    },
    availableMarkets() {
      return this.$store.getters.availableMarkets
    },
    marketTotalCostComparison() {
      _.forEach(this.$store.getters.marketCostComparison, (m, idx) => {
        this.updateMarketCosts(m, idx)
      });

      return _.map(this.$store.getters.marketCostComparison, (o, idx) => {
        o.name = this.markets[idx] ? this.markets[idx].market.name : ''
        return o
      })
    },
    marketBreakdownCostComparison() {
      return _.map(this.$store.getters.marketCostComparison, o => {
        let total = o.costs.laborCost + o.costs.realEstateCost + o.costs.inboundTransportationCost + o.costs.outboundTransportationCost
        return {
          marketId: o.marketId,
          name: this.availableMarkets.find(i => i.id == o.marketId).name,
          costs: {
            laborCost: o.costs.laborCost / total,
            realEstateCost: o.costs.realEstateCost / total,
            inboundTransportationCost: o.costs.inboundTransportationCost / total,
            outboundTransportationCost: o.costs.outboundTransportationCost / total
          }
        }
      })
    },
    nodes() {   
      return this.markets.map(o => {
        return {
          type: 'warehouse',
          name: o.market.name,
          position: {
            lat: o.market.latitude,
            lng: o.market.longitude
          }
        }
      })
    },
    sessionUsageData() {
      return this.$store.getters.sessionUsageData
    }
  },

  methods: {
    goToLanding() {
      this.$parent.$parent.$data.pagingEnabled = true
      this.$router.push({ path: '/'})
    },
    addLocationToCompare() {
      let m = _.cloneDeep(this.markets)
      let cm = _.cloneDeep(this.customMarkets)
      let randomId = "0_" + Math.floor(Math.random() * 100000);
      let firstAvailableMarket = _.cloneDeep(this.$store.getters.availableMarkets[0])
      
      if(firstAvailableMarket.id == 0){
        firstAvailableMarket.id = randomId
      }
      
      m.push({ market: firstAvailableMarket, totalCost: undefined, realEstateCost: undefined, laborCost: undefined, transportationCost: undefined })
      cm.push(firstAvailableMarket)
      this.updateMarketParams(firstAvailableMarket, m)
        .then(() => {
          this.$store.commit('setMarketsToCompare', m)
          this.$store.commit('setCustomMarkets', cm)
        })
    },
    deleteLocation(idx) {
      let m = _.cloneDeep(this.markets)
      let mp = _.cloneDeep(this.marketParams)
      let cm = _.cloneDeep(this.customMarkets)
      delete mp[m[idx].market.id]
      cm.splice(idx, 1)

      m.splice(idx, 1)
      this.$store.commit('setMarketsToCompare', m)
      this.$store.commit('setMarketParams', mp)
      this.$store.commit('setCustomMarkets', cm)
    },
    applyDetail(params) {
      this.showCostComparisonDetail = false
      this.$store.commit('setMarketParams', params)
      this.getMarketComparisonData()
    },
    handleResize() {
      this.innerHeight = window.innerHeight
      this.innerWidth = window.innerWidth

      this.calculatedHeight = (this.innerHeight - this.$refs.graph1.offsetTop - 30) / 2
    },
    selectedMarketDidChange(ev) {
      let mpInitializedIfIsMarketRenamePromise = Promise.resolve()
      if(ev.isStandardMarketRename){
        mpInitializedIfIsMarketRenamePromise = this.initializeMarketParam(ev.market, false)
      }
    
      mpInitializedIfIsMarketRenamePromise.then(() => {
        let m = _.cloneDeep(this.markets)
        let mp = _.cloneDeep(this.marketParams)
        let cm = _.cloneDeep(this.customMarkets)
        // if it's a custom market, don't delete the market params
        if(parseInt(ev.ident) == 0 && m[0].market.id.indexOf("0_") != 0 && ev.market.id.indexOf("0_") != 0) {
          delete mp[m[0].market.id]
        }
        if(ev.market.id.indexOf("0_") == 0){
          cm[parseInt(ev.ident)] = ev.market
          if(!ev.isStandardMarketRename && ev.costPerContainerAtNearestPort){
            mp[ev.market.id].costPerContainer.default = ev.costPerContainerAtNearestPort
            mp[ev.market.id].costPerContainer.current = ev.costPerContainerAtNearestPort
          }
        }

        m[parseInt(ev.ident)] = { market: ev.market, totalCost: undefined, realEstateCost: undefined, laborCost: undefined, transportationCost: undefined}  

        this.$store.commit('setMarketParams', mp)
        this.updateMarketParams(ev.market, m)
          .then(() => {
            this.$store.commit('setMarketsToCompare', m)
            this.$store.commit('setCustomMarkets', cm)
          })
      })
    },
    getMarketComparisonData() {
      let mkts = this.markets.map(m => {
        return { id: m.market.id, params: this.marketParams[m.market.id] }
      })

      this.$store.dispatch('getMarketComparisonData', mkts)
    },
    updateMarketCosts(market, idx) {
      if(this.markets.length > idx) {
        this.markets[idx].totalCost = market.costs.inboundTransportationCost + market.costs.outboundTransportationCost + market.costs.laborCost + market.costs.realEstateCost
        this.markets[idx].realEstateCost = market.costs.realEstateCost
        this.markets[idx].laborCost = market.costs.laborCost
        this.markets[idx].transportationCost = market.costs.inboundTransportationCost + market.costs.outboundTransportationCost      
      }
    },
    updateSquareFootage(ev) {
      let m = _.cloneDeep(this.marketParams)

      _.forEach(m, o => {
        o.buildingSqft.default = o.buildingSqft.current = ev.val
      })
      this.totalSquareFootage = ev.val      
      this.$store.commit('setMarketParams', m)

      this.getMarketComparisonData()
    },
    updateHeadCount(ev) {
      let m = _.cloneDeep(this.marketParams)

      _.forEach(m, o => {
        o.numberFullTimeEmployees.default = o.numberFullTimeEmployees.current = ev.val
      })
      this.headCountPerWarehouse = ev.val
      this.$store.commit('setMarketParams', m)

      this.getMarketComparisonData()
    },
    updateContainersPerYear(ev) {
      let m = _.cloneDeep(this.marketParams)

      _.forEach(m, o => {
        o.containersPerYear.default = o.containersPerYear.current = ev.val
      })
      this.containersPerYear = ev.val
      this.$store.commit('setMarketParams', m)
      this.getMarketComparisonData()
    },
    formatAsCurrency(v, noDecimal) {
      let decimals = noDecimal ? 0 : 2
      return "$" + Number(v).toFixed(decimals).replace(/./g, function(c, i, a) {
        return i > 0 && c !== "." && (a.length - i) % 3 === 0 ? "," + c : c
      })
    },
    formatAsNumber(v) {
      return Number(v).toFixed(0).replace(/./g, function(c, i, a) {
        return i > 0 && c !== "." && (a.length - i) % 3 === 0 ? "," + c : c
      })
    },
    formatAsPercent(v) {
      return Number(v).toFixed(2).replace(/./g, function(c, i, a) {
        return i > 0 && c !== "." && (a.length - i) % 3 === 0 ? "," + c : c
      }) + "%"
    },
    didSelectExport() {
      let api = this.$store.getters.apiServer
      let marketsToExport = this.markets.map(m => {
        let address = ''
        let lat = m.market.latitude
        let lng = m.market.longitude 
        if(m.market.id.indexOf("0_") == 0 && m.market.city && m.market.state && (m.market.city != '?' || m.market.state != '?')){
          address = m.market.city + ", " + m.market.state
        } else if(m.market.id.indexOf("0_") == 0 && m.market.locationString && m.market.locationString.indexOf(" - ") > 0){
          let stateAbbrMap = {"ALABAMA": "AL","ALASKA": "AK","AMERICAN SAMOA": "AS","ARIZONA": "AZ","ARKANSAS": "AR","CALIFORNIA": "CA","COLORADO": "CO","CONNECTICUT": "CT","DELAWARE": "DE","DISTRICT OF COLUMBIA": "DC","FEDERATED STATES OF MICRONESIA": "FM","FLORIDA": "FL","GEORGIA": "GA","GUAM": "GU","HAWAII": "HI","IDAHO": "ID","ILLINOIS": "IL","INDIANA": "IN","IOWA": "IA","KANSAS": "KS","KENTUCKY": "KY","LOUISIANA": "LA","MAINE": "ME","MARSHALL ISLANDS": "MH","MARYLAND": "MD","MASSACHUSETTS": "MA","MICHIGAN": "MI","MINNESOTA": "MN","MISSISSIPPI": "MS","MISSOURI": "MO","MONTANA": "MT","NEBRASKA": "NE","NEVADA": "NV","NEW HAMPSHIRE": "NH","NEW JERSEY": "NJ","NEW MEXICO": "NM","NEW YORK": "NY","NORTH CAROLINA": "NC","NORTH DAKOTA": "ND","NORTHERN MARIANA ISLANDS": "MP","OHIO": "OH","OKLAHOMA": "OK","OREGON": "OR","PALAU": "PW","PENNSYLVANIA": "PA","PUERTO RICO": "PR","RHODE ISLAND": "RI","SOUTH CAROLINA": "SC","SOUTH DAKOTA": "SD","TENNESSEE": "TN","TEXAS": "TX","UTAH": "UT","VERMONT": "VT","VIRGIN ISLANDS": "VI","VIRGINIA": "VA","WASHINGTON": "WA","WEST VIRGINIA": "WV","WISCONSIN": "WI","WYOMING": "WY"}
          let state = m.market.locationString.split(" - ")[0]
          let city = m.market.locationString.split(" - ")[1]
          state = stateAbbrMap[state] || state
          address = city + ", " + state
        } else if(m.market.id > 0 && m.market.name.indexOf(" - ") > 0){
          let stateAbbrMap = {"ALABAMA": "AL","ALASKA": "AK","AMERICAN SAMOA": "AS","ARIZONA": "AZ","ARKANSAS": "AR","CALIFORNIA": "CA","COLORADO": "CO","CONNECTICUT": "CT","DELAWARE": "DE","DISTRICT OF COLUMBIA": "DC","FEDERATED STATES OF MICRONESIA": "FM","FLORIDA": "FL","GEORGIA": "GA","GUAM": "GU","HAWAII": "HI","IDAHO": "ID","ILLINOIS": "IL","INDIANA": "IN","IOWA": "IA","KANSAS": "KS","KENTUCKY": "KY","LOUISIANA": "LA","MAINE": "ME","MARSHALL ISLANDS": "MH","MARYLAND": "MD","MASSACHUSETTS": "MA","MICHIGAN": "MI","MINNESOTA": "MN","MISSISSIPPI": "MS","MISSOURI": "MO","MONTANA": "MT","NEBRASKA": "NE","NEVADA": "NV","NEW HAMPSHIRE": "NH","NEW JERSEY": "NJ","NEW MEXICO": "NM","NEW YORK": "NY","NORTH CAROLINA": "NC","NORTH DAKOTA": "ND","NORTHERN MARIANA ISLANDS": "MP","OHIO": "OH","OKLAHOMA": "OK","OREGON": "OR","PALAU": "PW","PENNSYLVANIA": "PA","PUERTO RICO": "PR","RHODE ISLAND": "RI","SOUTH CAROLINA": "SC","SOUTH DAKOTA": "SD","TENNESSEE": "TN","TEXAS": "TX","UTAH": "UT","VERMONT": "VT","VIRGIN ISLANDS": "VI","VIRGINIA": "VA","WASHINGTON": "WA","WEST VIRGINIA": "WV","WISCONSIN": "WI","WYOMING": "WY"}
          let state = m.market.name.split(" - ")[0]
          let city = m.market.name.split(" - ")[1]
          state = stateAbbrMap[state] || state
          address = city + ", " + state
        }
        return { id: m.market.id, name: m.market.name, address: address, lat: lat, lng: lng, params: this.marketParams[m.market.id] }
      })
      api
        .post("/analysis/costscomparison/export", {
          annualRevenue: 0,
          squareFootage: 0,
          markets: marketsToExport
        }, { responseType: 'blob'})
        .then(resp => {
          fileDownload(resp.data, 'JLL-Site-Selector-Result.xlsx')
          this.$store.commit('serverRequestComplete')
        })
        .catch(err => {
          this.$store.commit('serverRequestComplete')
          console.log(err)
        })
    },
    didSelectImport(){
      this.$refs.hiddenImportFileInput.click()
    },
    importFileDialogClosed(e){
      if(!e || !e.target || !e.target.files) return
      
      let files = e.target.files
      let f = files[0]
      this.$refs.hiddenImportFileInput.value = ''
      this.processImportFile(f)
    },
    handleImportDrop(e) {
      e.preventDefault()
      e.stopPropagation()
      
      let files = e.dataTransfer.files
      let f = files[0]
      this.processImportFile(f)
    },
    processImportFile(file) {
      this.showFCCImportStatus = true
      this.fccImportInProgress = true
      this.fccImportErrors = []
      if(!file || file.name.indexOf(".xlsx") != file.name.length - 5){
        this.fccImportErrors.push({errType: "invalidFile"})
        this.fccImportInProgress = false
        return 
      }
      this.$store.dispatch('getNetworkCostsComparisonFromFile', file)
        .then((res) => {
          if(!res || !res.length){
            this.fccImportErrors.push({errType: "noResults"})
            this.fccImportInProgress = false
            return 
          }
          let availableMarkets = _.cloneDeep(this.$store.getters.availableMarkets)
          let newMarkets = []
          let newCustomMarkets = []
          let newMarketParams = {}
          res.reduce((p, marketParam, index) => { 
            return p.then(() => {
              let randomId = "0_" + Math.floor(Math.random() * 100000)
              let geocodedLocationPromise = Promise.resolve()
              let newMarket = null
              
              let matchedMarket = availableMarkets.find((market) => {
                return market.name == marketParam.marketName
              })
              let hasLatLng = (marketParam.latitude || marketParam.latitude == 0) && (marketParam.longitude || marketParam.longitude == 0)
              let hasLocationString = marketParam.locationString && marketParam.locationString.length
              if(matchedMarket){
                newMarket = matchedMarket
              } else {
                newMarket = {
                  id: randomId,
                  name: "Add a Custom Market",
                  latitude: 33.85,
                  longitude: -84.39,
                  laborRate: 0,
                  leaseRate: 0
                }
                if(marketParam.marketName && marketParam.marketName.indexOf("EMPTY_NAME_") < 0){
                  newMarket.name = marketParam.marketName
                }
                if(hasLatLng){
                  newMarket.latitude = marketParam.latitude
                  newMarket.longitude = marketParam.longitude
                }
                if(hasLocationString && !hasLatLng){
                  geocodedLocationPromise = this.$store.dispatch('geocodeLocation', marketParam.locationString)
                    .then((latLng) => {
                      if(!latLng || !latLng.latitude || !latLng.longitude){
                        return this.fccImportErrors.push({errType: "failedGeocode", marketName: marketParam.marketName})
                      }
                      newMarket.latitude = latLng.latitude
                      newMarket.longitude = latLng.longitude
                      newMarket.city = latLng.city
                      newMarket.state = latLng.state
                    })
                } else if(hasLocationString && hasLatLng){
                  let city = marketParam.locationString.split(",").length >= 1 ? marketParam.locationString.split(",")[0].trim() : ""
                  let state = marketParam.locationString.split(",").length >= 2 ? marketParam.locationString.split(",")[1].trim() : ""
                  newMarket.city = city
                  newMarket.state = state
                } else if(!hasLocationString && !hasLatLng){
                  this.fccImportErrors.push({errType: "noGeocodeParam", marketName: marketParam.marketName})
                }
              }
              return geocodedLocationPromise.then(() => {
                if(newMarket){
                  if(newMarket.id.indexOf("0_") == 0){
                    newCustomMarkets.push(newMarket)
                  } else {
                    newCustomMarkets.push({
                      id: randomId,
                      name: "Add a Custom Market",
                      latitude: 33.85,
                      longitude: -84.39,
                      laborRate: 0,
                      leaseRate: 0
                    })
                  }
                  newMarkets.push({ market: newMarket, totalCost: undefined, realEstateCost: undefined, laborCost: undefined, transportationCost: undefined })
                  newMarketParams[newMarket.id] = {
                    marketId: newMarket.id,
                    buildingSqft: { default: marketParam.buildingSqft || 0, current: marketParam.buildingSqft || 0, formatter: this.formatAsNumber },
                    rentPerSqftAnnual: { default: marketParam.rentPerSqftAnnual || 0, current: marketParam.rentPerSqftAnnual || 0, formatter: this.formatAsCurrency },
                    startupCost: { default: marketParam.startupCost || 0, current: marketParam.startupCost || 0, formatter: this.formatAsCurrency },
                    annualOperatingExpensesPerSqft: { default: marketParam.annualOperatingExpensesPerSqft || 0, current: marketParam.annualOperatingExpensesPerSqft || 0, formatter: this.formatAsCurrency },
                    annualOperatingExpenses: { default: marketParam.annualOperatingExpenses || 0, current: marketParam.annualOperatingExpenses || 0, formatter: this.formatAsCurrency },
                    tenantImprovementAllowance: { default: marketParam.tenantImprovementAllowance || 0, current: marketParam.tenantImprovementAllowance || 0, formatter: this.formatAsCurrency },
                    leaseDiscountRate: { default: marketParam.leaseDiscountRate || 0, current: marketParam.leaseDiscountRate || 0, formatter: this.formatAsPercent },
                    containersPerYear: { default: marketParam.containersPerYear || 0, current: marketParam.containersPerYear || 0, formatter: this.formatAsNumber },
                    costPerContainer:  { default: marketParam.costPerContainer || 0, current: marketParam.costPerContainer || 0, formatter: this.formatAsCurrency },
                    fuelSurcharge: { default: marketParam.fuelSurcharge || 0, current: marketParam.fuelSurcharge || 0, formatter: this.formatAsPercent },
                    additionalFreightCosts: { default: marketParam.additionalFreightCosts || 0, current: marketParam.additionalFreightCosts || 0, formatter: this.formatAsPercent },
                    airFreightCost: { default: marketParam.airFreightCost || 0, current: marketParam.airFreightCost|| 0, formatter: this.formatAsCurrency },
                    outboundLogisticsCost: { default: marketParam.outboundLogisticsCost || 0, current: marketParam.outboundLogisticsCost || 0, formatter: this.formatAsCurrency },
                    numberFullTimeEmployees: { default: marketParam.numberFullTimeEmployees || 0, current: marketParam.numberFullTimeEmployees || 0, formatter: this.formatAsNumber },
                    fullTimeHourlyWage: { default: marketParam.fullTimeHourlyWage || 0, current: marketParam.fullTimeHourlyWage || 0, formatter: this.formatAsCurrency },
                    fullTimeHoursPerWeek: { default: marketParam.fullTimeHoursPerWeek || 0, current: marketParam.fullTimeHoursPerWeek || 0, formatter: this.formatAsNumber },
                    fullTimeWeeksPerYear: { default: marketParam.fullTimeWeeksPerYear || 0, current: marketParam.fullTimeWeeksPerYear || 0, formatter: this.formatAsNumber },
                    numberTempEmployees: { default: marketParam.numberTempEmployees || 0, current: marketParam.numberTempEmployees || 0, formatter: this.formatAsNumber },
                    tempHoursPerWeek: { default: marketParam.tempHoursPerWeek || 0, current: marketParam.tempHoursPerWeek || 0, formatter: this.formatAsNumber },
                    numberSupervisorEmployees: { default: marketParam.numberSupervisorEmployees || 0, current: marketParam.numberSupervisorEmployees || 0, formatter: this.formatAsNumber },
                    numberAdminEmployees: { default: marketParam.numberAdminEmployees || 0, current: marketParam.numberAdminEmployees || 0, formatter: this.formatAsNumber },
                    tempHourlyWage: { default: marketParam.tempHourlyWage || 0, current: marketParam.tempHourlyWage || 0, formatter: this.formatAsCurrency },
                    supervisorSalary: { default: marketParam.supervisorSalary || 0, current: marketParam.supervisorSalary || 0, formatter: this.formatAsCurrency },
                    adminSalary: { default: marketParam.adminSalary || 0, current: marketParam.adminSalary || 0, formatter: this.formatAsCurrency },
                    benefits: { default: marketParam.benefits || 0, current: marketParam.benefits || 0, formatter: this.formatAsPercent },
                    workersCompensationPercentage: { default: marketParam.workersCompensationPercentage || 0, current: marketParam.workersCompensationPercentage || 0, formatter: this.formatAsPercent },
                    attritionRate: { default: marketParam.attritionRate || 0, current: marketParam.attritionRate || 0, formatter: this.formatAsPercent },
                    replacementCost: { default: marketParam.replacementCost || 0, current: marketParam.replacementCost || 0, formatter: this.formatAsCurrency },
                  }
                  if(marketParam.errors && marketParam.errors.length){
                    this.fccImportErrors = this.fccImportErrors.concat(marketParam.errors)
                  }
                }
              })
            })
          }, Promise.resolve())
          .then(() => {
            this.$store.commit('setCustomMarkets', newCustomMarkets)
            this.$store.commit('setMarketParams', newMarketParams)
            this.$store.commit('setMarketsToCompare', newMarkets)
            this.getMarketComparisonData()
            this.fccImportInProgress = false
          })
        })
    },
    initializeMarketParam(market, isCustomMarket){
      if(!market || (!market.latitude && market.latitude != 0) || (!market.longitude && market.longitude == 0)) {
        return Promise.reject()
      }
      return this.$store.dispatch('calculateCostPerContainerAtNearestPort', {latitude: market.latitude, longitude: market.longitude})
        .then(costPerContainerAtNearestPort => {
          let newMarketParams = _.cloneDeep(this.marketParams)
          let populationCentroidLatLng = { latitude: 0.710052649, longitude: -1.563650216 }
          let marketLatInRadians = Math.PI * market.latitude/180.0
          let marketLngInRadians = Math.PI * market.longitude/180.0
          let outboundLogisticsCost = 4000000 + (1000 * 6371 * 
            Math.acos(
              (Math.sin(marketLatInRadians) * Math.sin(populationCentroidLatLng.latitude)) 
              + (Math.cos(marketLatInRadians) * Math.cos(populationCentroidLatLng.latitude) * Math.cos(populationCentroidLatLng.longitude - marketLngInRadians))
            ))
          newMarketParams[market.id] = {
            marketId: market.id,
            buildingSqft: { default: this.totalSquareFootage, current: this.totalSquareFootage, formatter: this.formatAsNumber },
            rentPerSqftAnnual: { default: market.leaseRate, current: market.leaseRate, formatter: this.formatAsCurrency },
            startupCost: { default: 0, current: 0, formatter: this.formatAsCurrency },
            annualOperatingExpensesPerSqft: { default: 0, current: 0, formatter: this.formatAsCurrency },
            annualOperatingExpenses: { default: 0, current: 0, formatter: this.formatAsCurrency },
            tenantImprovementAllowance: { default: 0, current: 0, formatter: this.formatAsCurrency },
            leaseDiscountRate: { default: 0, current: 0, formatter: this.formatAsPercent },
            containersPerYear: { default: this.containersPerYear || 1200, current: this.containersPerYear || 1200, formatter: this.formatAsNumber },
            costPerContainer:  { default: costPerContainerAtNearestPort, current: costPerContainerAtNearestPort, formatter: this.formatAsCurrency },
            fuelSurcharge: { default: 0, current: 0, formatter: this.formatAsPercent },
            additionalFreightCosts: { default: 0, current: 0, formatter: this.formatAsPercent },
            airFreightCost: { default: 0, current: 0, formatter: this.formatAsCurrency },
            outboundLogisticsCost: { default: isCustomMarket ? 0 : outboundLogisticsCost, current: isCustomMarket ? 0 : outboundLogisticsCost, formatter: this.formatAsCurrency },
            numberFullTimeEmployees: { default: this.headCountPerWarehouse, current: this.headCountPerWarehouse, formatter: this.formatAsNumber },
            fullTimeHourlyWage: { default: market.laborRate, current: market.laborRate, formatter: this.formatAsCurrency },
            fullTimeHoursPerWeek: { default: 40, current: 40, formatter: this.formatAsNumber },
            fullTimeWeeksPerYear: { default: 50, current: 50, formatter: this.formatAsNumber },
            numberTempEmployees: { default: 0, current: 0, formatter: this.formatAsNumber },
            tempHoursPerWeek: { default: 0, current: 0, formatter: this.formatAsNumber },
            numberSupervisorEmployees: { default: 0, current: 0, formatter: this.formatAsNumber },
            numberAdminEmployees: { default: 0, current: 0, formatter: this.formatAsNumber },
            tempHourlyWage: { default: 0, current: 0, formatter: this.formatAsCurrency },
            supervisorSalary: { default: 0, current: 0, formatter: this.formatAsCurrency },
            adminSalary: { default: 0, current: 0, formatter: this.formatAsCurrency },
            benefits: { default: 0, current: 0, formatter: this.formatAsPercent },
            workersCompensationPercentage: { default: 0, current: 0, formatter: this.formatAsPercent },
            attritionRate: { default: 25, current: 25, formatter: this.formatAsPercent },
            replacementCost: { default: 0, current: 0, formatter: this.formatAsCurrency },
          }

          this.$store.commit('setMarketParams', newMarketParams)
        })
    },
    updateMarketParams(market, markets) {
      let marketParamPromise = Promise.resolve()
      if(market && this.marketParams[market.id] == undefined) {
        let isCustomMarket = market.id.indexOf("0_") == 0;
        marketParamPromise = this.initializeMarketParam(market, isCustomMarket)
      }
      
      return marketParamPromise.then(() => {
        let newMarketParams = _.cloneDeep(this.marketParams)

        // apply adjustments from baseline
        if(markets.length > 0) {
          let baseline = newMarketParams[markets[0].market.id]
          let leaseAdj = baseline.rentPerSqftAnnual.current / baseline.rentPerSqftAnnual.default
          let wageAdj  = baseline.fullTimeHourlyWage.current / baseline.fullTimeHourlyWage.default
          leaseAdj = isNaN(leaseAdj) || leaseAdj == "Infinity" ? 1 : leaseAdj
          wageAdj = isNaN(wageAdj) || wageAdj == "Infinity" ? 1 : wageAdj

          _.tail(markets).forEach(m => {
            let p = newMarketParams[m.market.id]
            p.rentPerSqftAnnual.current = p.rentPerSqftAnnual.default * leaseAdj
            p.fullTimeHourlyWage.current = p.fullTimeHourlyWage.default * wageAdj
            p.tempHourlyWage.current = p.tempHourlyWage.default * wageAdj
            p.supervisorSalary.current = p.supervisorSalary.default * wageAdj
            p.adminSalary.current = p.adminSalary.default * wageAdj
          })
        }

        this.$store.commit('setMarketParams', newMarketParams)
      })
    },
    startSessionRefreshingInterval() {
      return setInterval(() => {
        if(this.$cookies.get("sessionInitStatus") != "done"){
          return this.$store.dispatch('initializeSessionDetails')
        }
        this.$store.dispatch('refreshSessionTokens')
        this.$store.dispatch('updateSessionEndTime')
      }, 1 * 60 * 1000)
    },
    keepSessionActive() {
      if(this.sessionIdleTimeout){
        clearTimeout(this.sessionIdleTimeout)
      }
      this.sessionIdleTimeout = setTimeout(() => {
        this.cancelSessionRefreshingInterval()
        this.$store.dispatch('closeSession')
      }, 30 * 60 * 1000)
      if(!(this.$cookies.isKey("sessionInitStatus") && this.$cookies.isKey("sessionId"))){
        this.$store.dispatch('initializeSessionDetails')
        this.cancelSessionRefreshingInterval()
        this.sessionRefreshingInterval = this.startSessionRefreshingInterval()
      }
    },
    cancelSessionRefreshingInterval() {
      if(this.sessionRefreshingInterval){
        clearInterval(this.sessionRefreshingInterval)
      }
    },
    updateZoom(v) {
      this.pdfMapOptions.zoomLevel = v
    },
    updateCenter(v) {
      this.pdfMapOptions.mapCenter = v
    },
    getSessionUsageData() {
      if(!this.sessionUsageData || !this.sessionUsageData.customerName || !this.sessionUsageData.customerName.length 
        || !this.sessionUsageData.usePurpose || !this.sessionUsageData.usePurpose.length){
        this.showSessionUsageSurvey = true
        return new Promise((resolve, reject) => {
          this.resolveSessionUsageData = { resolve: resolve, reject: reject }
        })
      } else {
        return Promise.resolve()
      }
    },
    applySessionUsageSurvey(v) {
      if(this.lastIncompleteSession){
        v.sessionId = this.lastIncompleteSession.sessionId
        this.$store.dispatch('updateSessionUsageData', v)
        this.lastIncompleteSession = null
        this.$cookies.remove('lastIncompleteSession')
      } else {
        this.$store.commit('setSessionUsageData', v)
        this.$store.dispatch('updateSessionUsageData', v)
        this.$store.dispatch('markSessionComplete', v)
        this.resolveSessionUsageData.resolve()
      }
      this.showSessionUsageSurvey = false
    },
    closeSessionUsageSurvey() {
      if(this.lastIncompleteSession){
        this.lastIncompleteSession = null
      } else {
        this.resolveSessionUsageData.reject()
      }
      this.showSessionUsageSurvey = false
    },
    async generatePdfReport() {
      let sessionUsageDataPromise = this.getSessionUsageData()
      let barChart = document.querySelector('.pdf-your-costs-chart #bar-chart').toDataURL()
      
      let currentUser = await this.$auth.getUser()
      let currentUserName = (currentUser.name && currentUser.name.length 
        ? currentUser.name 
        : (currentUser.family_name || currentUser.given_name || "")
      )

      let barChartPdfWidth = 750
      let barChartPdfHeight = 400

      let staticMapImageWidth = 600
      let staticMapImageHeight = 320
      let staticMapPdfWidth = 750
      let staticMapPdfHeight = 400

      let nodeMarkers = ""
      let minLng = 180
      let maxLng = -180
      let minLat = 90
      let maxLat = -90
      this.nodes.forEach((node, index) => {
         if(index == 0){
           nodeMarkers += "&markers=scale:2|anchor:bottom|icon:"+window.location.origin+"/icon_warehouse.png"
         }
         nodeMarkers += "|" + node.position.lat + "," + node.position.lng;
         minLng = minLng > node.position.lng ? node.position.lng : minLng
         maxLng = maxLng < node.position.lng ? node.position.lng : maxLng
         minLat = minLat > node.position.lat ? node.position.lat : minLat
         maxLat = maxLat < node.position.lat ? node.position.lat : maxLat
      })

      let midrangeLat = ((minLat + maxLat)/2) || 38.768403
      let midrangeLng = ((minLng + maxLng)/2) || -96.0
      let latDiff = Math.abs(maxLat - minLat)
      let lngDiff = Math.abs(maxLng - minLng)

      let staticMapZoomLevel = 3
      if(lngDiff > 50 || latDiff > 18){
        staticMapZoomLevel = 3
      } else if(lngDiff > 25 || latDiff > 9){
        staticMapZoomLevel = 4
      } else {
        staticMapZoomLevel = 5
      }

      let staticMapUrl = "https://maps.googleapis.com/maps/api/staticmap"
      staticMapUrl += "?center=" + midrangeLat + "," + midrangeLng;
      staticMapUrl += "&scale=2"
      staticMapUrl += "&size=" + staticMapImageWidth + "x" + staticMapImageHeight;
      staticMapUrl += "&zoom=" + staticMapZoomLevel;
      staticMapUrl += this.pdfMapOptions.styleParams;
      staticMapUrl += "&key=AIzaSyDjFmm6MQ3TZIUGm9WtsnpFjPCj6P9YySY"
      staticMapUrl += nodeMarkers

      let staticMapDataUrl = await fetch(staticMapUrl)
        .then(r => r.blob())
        .then(blob => {
          return new Promise(resolve => {
            let reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
        })
      
      let marketNames = this.marketTotalCostComparison.map( m => { 
        return { text: m.name, style: 'contentTableSubheader' } 
      })
      let buildingSqftRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsNumber(this.marketParams[m.marketId].buildingSqft.current), style: 'contentTableBody' } 
      })
      let annualLeaseRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsCurrency(this.marketParams[m.marketId].rentPerSqftAnnual.current), style: 'contentTableBody' } 
      })
      let headcountRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsNumber(this.marketParams[m.marketId].numberFullTimeEmployees.current), style: 'contentTableBody' }
      })
      let hourlyWageRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsCurrency(this.marketParams[m.marketId].fullTimeHourlyWage.current), style: 'contentTableBody' }
      })
      let containersPerYearRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsNumber(this.marketParams[m.marketId].containersPerYear.current), style: 'contentTableBody' } 
      })
      let costPerContainerRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsCurrency(this.marketParams[m.marketId].costPerContainer.current), style: 'contentTableBody' } 
      })
      
      let outboundFreightRow = this.marketTotalCostComparison.map(m => { 
        return { text: this.formatAsCurrency(m.costs.outboundTransportationCost, true), style: 'contentTableBody' } 
      })
      let inboundFreightRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsCurrency(m.costs.inboundTransportationCost, true), style: 'contentTableBody' } 
      })
      let laborRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsCurrency(m.costs.laborCost, true), style: 'contentTableBody' } 
      })
      let leaseRow = this.marketTotalCostComparison.map( m => { 
        return { text: this.formatAsCurrency(m.costs.realEstateCost, true), style: 'contentTableBody' } 
      })
      
      let marketTotals = this.marketTotalCostComparison.map( m => {
        let rowTotal = m.costs.outboundTransportationCost + m.costs.inboundTransportationCost
          + m.costs.laborCost + m.costs.realEstateCost
        return rowTotal
      })
      let outputsTotalRow = marketTotals.map( t => {
        return { text: this.formatAsCurrency(t, true), style: 'contentTableSubheader', bold: true }
      })
      let outputsRankRow = marketTotals.map( (t, i) => {
        let rank = 1
        marketTotals.forEach( (innerT, innerI) => {
          if(i != innerI && t > innerT) {
            rank++
          }
        })
        return { text: rank, style: 'contentTableBody', bold: true }
      })
      
      sessionUsageDataPromise.then(() => {
        let hasCustomerName = this.sessionUsageData && this.sessionUsageData.customerName && this.sessionUsageData.customerName.length
        let headerHeight = 95
        let footerHeight = 40
        let sideMargin = 30
        
        let currentDate = new Date()
        let monthName = ['January','February','March','April','May','June','July','August','September','October','November','December']
        
        let docJson = {
          pageMargins: [sideMargin, headerHeight, sideMargin, footerHeight],
          pageOrientation: 'landscape',
          header: {
            layout: 'noBorders',
            table: {
              heights: [headerHeight],
              widths: ['*',150],
              body: [
                [
                  [
                    {
                      text: "Industrial Site Selector",
                      alignment: "left",
                      fontSize: 22,
                      bold: true,
                      margin: [30, 15, 0, 0]
                    },
                    {
                      text: (hasCustomerName ? "Client Name: " + this.sessionUsageData.customerName : ""),
                      alignment: "left",
                      fontSize: 18,
                      margin: [30, 0, 0, 0]
                    }
                  ],
                  [
                    {
                      image: this.logoImage,
                      width: 70,
                      alignment: "right",
                      margin: [0, 30, 15, 0]
                    },
                    {
                      text: "Created " + currentDate.getDate() + " " + monthName[currentDate.getMonth()] + " " + currentDate.getFullYear(),
                      alignment: "right",
                      fontSize: 8,
                      margin: [0, 15, 30, 0]
                    }
                  ]
                ]
              ]
            },
            height: headerHeight
          },
          footer: {
            layout: 'noBorders',
            table: {
              heights: [footerHeight],
              widths: [350,'*'],
              body: [
                [
                  {
                    text: "© 2020 Jones Lang LaSalle IP, Inc. All rights Reserverd.",
                    alignment: "left",
                    fontSize: 8,
                    margin: [30, 10, 0, 0]
                  },
                  [
                    {
                      text: "Created by: " + (currentUserName || ""),
                      alignment: "right",
                      fontSize: 8,
                      bold: true,
                      margin: [0, 0, 25, 0]
                    },
                    {
                      text: "www.us.jll.com",
                      alignment: "right",
                      fontSize: 8,
                      bold: true,
                      margin: [0, 0, 25, 0]
                    }
                  ]
                ]
              ]
            },
            height: 90
          },
          styles: {
            contentTableHeader: {
              color: '#ffffff',
              margin: [2,2,2,2]
            },
            contentTableSubheader: {
              color: '#ffffff',
              margin: [2,2,2,2]
            },
            contentTableBody: {
              margin: [2,2,2,2]
            }
          },
          background: function(page){
            return {
              image: 'data:image/png;base64,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',
              width: 860,
              height: 85,
              
            }
          },
          content: [
            {
              table: {
                widths: [100].concat(marketNames.map( m => '*' )),
                body: [
                  [{ text: 'INPUTS', style: 'contentTableHeader' }].concat(marketNames.map( m => '' )),
                  [''].concat(marketNames),
                  [{ text: 'Building Square Feet', style: 'contentTableBody' }].concat(buildingSqftRow),
                  [{ text: 'Annual Lease Per sq. ft.', style: 'contentTableBody' }].concat(annualLeaseRow),
                  [{ text: 'Headcount', style: 'contentTableBody' }].concat(headcountRow),
                  [{ text: 'Full Time Hourly Wage', style: 'contentTableBody' }].concat(hourlyWageRow),
                  [{ text: 'Containers Per Year', style: 'contentTableBody' }].concat(containersPerYearRow),
                  [{ text: 'Cost Per Container', style: 'contentTableBody' }].concat(costPerContainerRow),
                  [{ text: 'OUTPUTS', style: 'contentTableHeader' }].concat(marketNames.map( m => '' )),
                  [''].concat(_.cloneDeep(marketNames)),
                  [{ text: 'Outbound Freight', style: 'contentTableBody' }].concat(outboundFreightRow),
                  [{ text: 'Inbound Freight', style: 'contentTableBody' }].concat(inboundFreightRow),
                  [{ text: 'Labor', style: 'contentTableBody' }].concat(laborRow),
                  [{ text: 'Lease', style: 'contentTableBody' }].concat(leaseRow),
                  [{ text: "Total", style: 'contentTableSubheader', bold: true }].concat(outputsTotalRow),
                  [{ text: 'Rank', style: 'contentTableBody', bold: true }].concat(outputsRankRow),
                ]
              },
              layout: {
                fillColor: function (rowIndex, node, columnIndex) {
                  if(rowIndex == 0 || rowIndex == 8) {
                    return '#E30613'
                  } else if(rowIndex == 1 || rowIndex == 9 || rowIndex == 14) {
                    return '#B1B2B4'
                  } else if(rowIndex % 2 == 1){
                    return '#E7E7E7'
                  }
                },
                vLineWidth: function(index, node) { return 0 },
                hLineWidth: function(index, node) { return 0 }
              },
              pageBreak: 'after'
            },
            {
              columns: [
                {
                  table: {
                    widths: [750],
                    heights: [400],
                    body: [
                      [{
                        image: barChart,
                        width: barChartPdfWidth,
                        height: barChartPdfHeight
                      }]
                    ]
                  },
                  alignment: "center"
                }
              ],
              margin: [0,10,0,0],
              pageBreak: 'after'
            },
            {
              columns: [
                {
                  table: {
                    widths: [750],
                    heights: [400],
                    body: [
                      [{
                        image: staticMapDataUrl,
                        width: staticMapPdfWidth,
                        height: staticMapPdfHeight
                      }]
                    ]
                  },
                  width: staticMapPdfWidth,
                  height: staticMapPdfHeight
                }
              ],
              margin: [10,20,0,0]
            }
          ],
          defaultStyle: {
            fontSize: 8
          }
        }
        
        let doc = pdfMake.createPdf(docJson)
        doc.getDataUrl((dataUrl) => this.generatedPdfReport = dataUrl)
        
        this.showPdfReportViewer = true
      }).catch((err) => {
        console.log(err)
        this.showPdfReportViewer = false
      })
    }
  }
}

</script>




<style scoped>
h1, h2 {
  font-weight: normal;
  color: #707070;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a:link, a:visited, a:active {
  text-decoration: none;
}
a:hover {
  font-weight: bold;
}

.app {
  height: 100%;
  overflow: scroll;
}

.grid {
  display: grid;
  text-align: left;
  font-size: 13pt;
  min-height: 100%;
  max-height: 100%;
  width: 100%;
  grid-gap: 0.5em;
  grid-template-columns: 1fr 1fr;
	grid-template-rows: 56px 165px 1fr 1fr;
}
.adjust-actions {
  display: inline-block;
  font-size: 10pt;
  position: absolute;
  right: 180px;
  top: 5px;
}
.section-title {
  text-align: left;
  padding-left: 10px;
  color: #444;
}
.navigator-link {
  position: absolute;
  right: 13px;
  top: 3px;
  font-size: 10pt;
}
.adjust-section-title {
  font-weight: bold;
  font-size: 13pt;
}
.input-card-content {
  width: calc(100% - 162px);
  height: 100%;
}
.vertical-separator {
  width: 1px;
  height: 35px;
  background-color: #ccc;
  position: relative;
  top: 35px;
}
.section-title-action {
  position: absolute;
  top: 3px;
  right: 20px;
  font-size: 10pt;
}

#return-button {
  grid-column: 1 / -1;
  width: 100%;
  text-align: left;
  padding: 5px;
}
#header {
  grid-column: 1 / -1;
  grid-row: 2 / 3;
}
#graph1 {
  grid-column: 2 / -1; 
  grid-row: 3 / -1;
  min-width: 100%; 
}
#graph2 {
  grid-column: 2 / -1;
  min-width: 100%; 
}
#facility-view {
  grid-column: 1 / 2;
  grid-row: 3 / 4;
  min-width: 100%; 
}
#facility-list {
  padding-top: 0;
  margin-top: 0;
  width: 100%;
  height: calc(100% - 60px);
  overflow-y: scroll;
  overflow-x: hidden;
}
#sbmap {
  grid-column: 1 / 2;
  grid-row: 4 / -1;
}
.map-view {
  height: 100%;
}
.map {
  padding: 5px 8px 31px 8px;
}
.input-view {
  height: 165px;
}
.input-card {
  height: 100%;
}
.your-costs-view { 
  height: 100%
}
.your-costs-chart { 
  height: calc(100% - 26px);
  padding-left: 15px;
  padding-right: 15px;
  padding-bottom: 5px;
}
.your-service-view {
  height: 100%;
}
.your-service-chart {
  height: calc(100% - 26px);
  padding-left: 15px;
  padding-right: 15px;
  padding-bottom: 5px;
}
#fcc-container {
  padding-top: 0px;
}
.nav-up-button {
  height: 40px;
  width: 40px;
}
.clickable {
  cursor: pointer;
}

@media only screen and (max-width: 1000px) {
  .grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media screen and (max-width: 600px) , screen and (max-height: 550px) {
  .help-box {
    display: none;
  }
  .adjust-actions {
    right: 5px;
  }
  #graph1 {
    grid-column: 1/3;
    grid-row: 5 / -1;
  }
  #graph2 {
    grid-column: 1/3;
  }
  #sbmap {
    grid-column: 1/3;
    grid-row: 4;
  }
  #facility-list {
    height: 150px;
    overflow-y: scroll;
  }
  #facility-view {
		grid-column: 1/3;
    grid-row: 3;
  }
  .app {
    height: 2000px;
  }
	.grid {
    grid-template-rows: 56px 165px 185px auto auto;
  }
  .input-card-content {
    width: 100%;
  }
}
.rates-updated {
  text-align: left !Important;
  padding-left: 10px;
  color: #3A3B3C;
  font-size: 10pt;
}
</style>
